import { Badge, Button, Header, Stack } from '@asgard/ui-library';
import { faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import { faCheck } from '@fortawesome/pro-solid-svg-icons';
import { FormCheckbox } from 'components/form/FormCheckbox';
import { FormPhone } from 'components/form/FormPhone';
import { FormSelect } from 'components/form/FormSelect';
import { FormText } from 'components/form/FormText/FormText';
import { FormTextarea } from 'components/form/FormTextarea';
import { HashAnchor } from 'components/HashAnchor';
import FormGroup from 'components/shared/FormGroup';
import HtmlTrans from 'components/shared/HtmlTrans';
import { LinkText } from 'components/shared/LinkText';
import { PHONE_NUMBER_DEFAULT_COUNTRY } from 'features/portal/v2/contact/basic-info/ContactBasicInfo.constants';
import { Trans } from 'next-i18next';
import { Controller, FormProvider } from 'react-hook-form';
import { APP_URLS, getAppUrl } from 'utils/app-urls';
import { getPublicPath } from 'utils/paths';
import { useConsultancyForm } from './Form.hooks';
import {
  StyledContainer,
  StyledHeader,
  StyledHeroImage,
  StyledTerms,
  StyledWrapper
} from './Form.styled';
import { getTypeOptions } from './Form.utils';

export const Form: React.FC = () => {
  const {
    t,
    formMethods,
    locale,
    onSubmit,
    isTermsAccepted,
    onFormFieldFocus
  } = useConsultancyForm();

  const {
    control,
    handleSubmit,
    formState: { isSubmitting }
  } = formMethods;

  return (
    <StyledWrapper>
      <HashAnchor name="hrForm" />
      <StyledContainer onSubmit={handleSubmit(onSubmit)}>
        <StyledHeader>
          <Header as="h2" size="4xl" editorial>
            {t('hr-as-a-service.form.title')}
          </Header>
          <HtmlTrans ns="hr-as-a-service" i18nKey="hr-as-a-service.form.desc" />
        </StyledHeader>
        <FormProvider {...formMethods}>
          <FormGroup
            label={t('hr-as-a-service.form.packages')}
            forId={'packages'}
          >
            <Controller
              control={control}
              name={'packages'}
              render={({ field }) => (
                <Stack gap="m" wrap>
                  {Array.from(Array(8).keys()).map((_, index) => (
                    <Badge
                      bold
                      key={index}
                      variant="white"
                      size="s"
                      className={
                        field.value?.includes(
                          t(`hr-as-a-service.form.package${index + 1}`)
                        )
                          ? 'active'
                          : 'neutral'
                      }
                      schema={
                        field.value?.includes(
                          t(`hr-as-a-service.form.package${index + 1}`)
                        )
                          ? 'active'
                          : 'neutral'
                      }
                      icon={
                        field.value?.includes(
                          t(`hr-as-a-service.form.package${index + 1}`)
                        )
                          ? faCheck
                          : undefined
                      }
                      onClick={() =>
                        field.onChange(
                          field.value?.includes(
                            t(`hr-as-a-service.form.package${index + 1}`)
                          )
                            ? field.value?.filter(
                                (v) =>
                                  v !==
                                  t(`hr-as-a-service.form.package${index + 1}`)
                              )
                            : [
                                ...new Set([
                                  ...(field.value || []),
                                  t(`hr-as-a-service.form.package${index + 1}`)
                                ])
                              ]
                        )
                      }
                      label={t(`hr-as-a-service.form.package${index + 1}`)}
                    />
                  ))}
                </Stack>
              )}
            />
          </FormGroup>

          <FormText
            name="name"
            formGroup={{
              label: t('hr-as-a-service.form.name')
            }}
            input={{
              placeholder: t('hr-as-a-service.form.name_placeholder'),
              onFocus: onFormFieldFocus
            }}
          />
          <FormText
            name="email"
            formGroup={{
              label: t('hr-as-a-service.form.email')
            }}
            input={{
              placeholder: t('hr-as-a-service.form.email_placeholder'),
              onFocus: onFormFieldFocus
            }}
          />
          <div className="phoneNumber">
            <FormPhone
              name="contactPhone"
              formGroup={{
                label: t('hr-as-a-service.form.phone')
              }}
              input={{
                defaultCountry: PHONE_NUMBER_DEFAULT_COUNTRY,
                onFocus: onFormFieldFocus
              }}
            />
          </div>
          <FormText
            name="company"
            formGroup={{
              label: t('hr-as-a-service.form.company')
            }}
            input={{
              placeholder: t('hr-as-a-service.form.company_placeholder'),
              onFocus: onFormFieldFocus
            }}
          />
          <FormSelect
            name="howDoYouKnowUs"
            formGroup={{
              label: t('hr-as-a-service.form.how-do-you-know-us')
            }}
            options={getTypeOptions(t)}
          />
          <FormTextarea
            name="description"
            formGroup={{
              label: t('hr-as-a-service.form.description')
            }}
            input={{
              placeholder: t('hr-as-a-service.form.description_placeholder'),
              onFocus: onFormFieldFocus,
              rows: 3
            }}
          />
          <FormCheckbox
            name="terms"
            input={{
              onFocus: onFormFieldFocus,
              label: (
                <StyledTerms>
                  <Trans
                    ns={'hr-as-a-service'}
                    i18nKey={'hr-as-a-service.form.terms'}
                    components={{
                      privatePolicy: (
                        <LinkText
                          href={getAppUrl(APP_URLS.privacyPolicy, locale)}
                        />
                      ),
                      terms: (
                        <LinkText
                          href={getAppUrl(APP_URLS.termsOfUse, locale)}
                        />
                      )
                    }}
                  ></Trans>
                </StyledTerms>
              )
            }}
          />

          <FormCheckbox
            name="isSubscribeToNewsletter"
            input={{
              onFocus: onFormFieldFocus,
              label: (
                <StyledTerms>
                  <Trans
                    ns={'hr-as-a-service'}
                    i18nKey={'hr-as-a-service.form.newsletter'}
                    components={{
                      newsletter: (
                        <a
                          href="https://none.clickacm.com/show/qgJ2lZXPBBizIfJi3pmS/"
                          target="_blank"
                        />
                      )
                    }}
                  ></Trans>
                </StyledTerms>
              )
            }}
          />
          <Button
            schema="brand2"
            label={t('hr-as-a-service.form.submit')}
            startIcon={faCheckCircle}
            type="submit"
            disabled={!isTermsAccepted}
            isLoading={isSubmitting}
            loadingText={t('hr-as-a-service.form.submit_in-progress')}
          />
        </FormProvider>
      </StyledContainer>
      <StyledHeroImage>
        <img
          src={getPublicPath('/images/consultancy/planet.svg')}
          alt=""
          className="planet"
        />
        <img
          src={getPublicPath('/images/consultancy/astronaut.svg')}
          alt=""
          className="astronaut"
        />
      </StyledHeroImage>
    </StyledWrapper>
  );
};
